"use client";

import React from "react";
import { ShieldCheck, Paintbrush, ThumbsUp } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const trustIndicators = [{
  title: "Fully Insured",
  description: "Full peace of mind for your property and complete protection throughout the project.",
  icon: ShieldCheck
}, {
  title: "Premium Materials",
  description: "We use only the highest quality paints and coatings for a durable, flawless finish.",
  icon: Paintbrush
}, {
  title: "100% Satisfaction Guarantee",
  description: "We don't consider the job done until you are completely happy with the results.",
  icon: ThumbsUp
}];
export default function OurCoastalLegacy() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-coastal-legacy" className="py-24 bg-background overflow-hidden relative">
      {}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none translate-x-1/4">
        <svg viewBox="0 0 200 600" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full text-secondary">
          <path d="M50 0C80 100 20 200 50 300C80 400 20 500 50 600" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M100 0C130 100 70 200 100 300C130 400 70 500 100 600" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div ref={imageRef} className={`relative transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5] sm:aspect-square lg:aspect-[4/5]">
              <img src="https://allowing-mallard.websiteshop.au/wp-content/uploads/2026/03/pexels-blue-bird-7217918-scaled.jpg" alt="Professional painting service on the Northern Central Coast" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent" />
              
              {}
              <div className="absolute bottom-8 left-8 right-8 p-6 bg-white/95 backdrop-blur-sm rounded-xl border border-white/20 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="size-12 rounded-full bg-accent flex items-center justify-center shrink-0">
                    <span className="font-bold text-primary text-xl">30+</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-primary leading-tight">Years Local Experience</h4>
                    <p className="text-sm text-muted-foreground">Serving the Central Coast since 1994</p>
                  </div>
                </div>
              </div>
            </div>
            
            {}
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-secondary/10 rounded-full blur-3xl -z-10" />
            <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-accent/10 rounded-full blur-3xl -z-10" />
          </div>

          {}
          <div ref={contentRef} className={`flex flex-col gap-10 transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20">
                <span className="text-xs font-bold uppercase tracking-wider text-secondary">Our Legacy</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-primary tracking-tight leading-[1.1]">
                Craftsmanship That Withstands the Coastal Elements
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                At Clint's Coastal Painting and Decorating, we understand the unique challenges of maintaining a beautiful home by the sea. With over three decades of hands-on local experience, Clint personally oversees every project to ensure the highest standards of quality, reliability, and precision.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5">
              {trustIndicators.map((indicator, i) => {
              const Icon = indicator.icon;
              return <Card key={i} data-index={i} className="border border-border/50 shadow-sm hover:shadow-xl hover:shadow-secondary/10 transition-all duration-300 group">
                    <CardHeader className="flex flex-row items-center gap-5 space-y-0 pb-2">
                      <div className="size-12 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0 transition-colors group-hover:bg-secondary/20">
                        <Icon className="size-6 text-secondary" strokeWidth={1.5} />
                      </div>
                      <CardTitle className="text-xl font-bold text-primary">
                        {indicator.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pl-[4.25rem] text-muted-foreground leading-relaxed">
                      {indicator.description}
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>

        </div>
      </div>
    </section>;
}